import { useQuery } from '@tanstack/react-query';
import { API_BASE_PATH } from '@catalyst-psa/shared';

import { env } from '../../config/env.js';
import { Button } from '../ui/Button.js';

/** Shape of the static /version.json emitted by the build. */
interface VersionFile {
  version: string;
  /** Backend SHA this frontend needs live before a refresh is safe (optional, newer builds). */
  requiredBackend?: string;
}

/** Combined deploy state used to decide whether to prompt a refresh. */
interface UpdateState {
  /** The frontend build currently deployed to the CDN. */
  deployedVersion: string | null;
  /** The backend SHA the deployed frontend requires (null when not published). */
  requiredBackend: string | null;
  /** The backend build currently live (null when /health is unreachable). */
  backendBuild: string | null;
}

/** The version baked into THIS running bundle at build time. */
const RUNNING_VERSION = __APP_VERSION__;

/** Fetches the deployed frontend version + its required backend SHA from /version.json. */
async function fetchDeployedVersion(): Promise<VersionFile | null> {
  try {
    const response = await fetch(`/version.json?ts=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    return (await response.json()) as VersionFile;
  } catch {
    return null;
  }
}

/** Fetches the live backend build SHA from the public /health endpoint. */
async function fetchBackendBuild(): Promise<string | null> {
  try {
    const response = await fetch(`${env.apiBaseUrl}${API_BASE_PATH}/health?ts=${Date.now()}`, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { build?: string };
    return typeof data.build === 'string' ? data.build : null;
  } catch {
    return null;
  }
}

/**
 * Polls the deployed frontend version (and its required backend) plus the live backend
 * build, so the banner can wait for the backend to catch up before prompting.
 */
async function fetchUpdateState(): Promise<UpdateState> {
  const [versionFile, backendBuild] = await Promise.all([
    fetchDeployedVersion(),
    fetchBackendBuild(),
  ]);
  return {
    deployedVersion: versionFile?.version ?? null,
    requiredBackend: versionFile?.requiredBackend ?? null,
    backendBuild,
  };
}

/**
 * ClickUp-style "you've been upgraded" prompt. Polls the deployed build identifier every
 * minute; when it differs from the version this tab is running, a non-blocking strip
 * invites the user to refresh.
 *
 * It is BACKEND-AWARE: a newer frontend publishes the backend SHA it needs
 * (`requiredBackend` in version.json), and the banner only appears once the backend's
 * `/health` build matches it. This prevents prompting a refresh into a frontend that is
 * newer than the live API during a combined deploy (the frontend deploys faster than the
 * backend's build → migrate → slot-swap). Frontend-only deploys leave the backend
 * unchanged, so `requiredBackend` points at the already-live backend SHA and the banner
 * appears immediately. Never shows in local dev (versions are `dev`).
 */
export function UpdateBanner(): JSX.Element | null {
  const { data } = useQuery({
    queryKey: ['app-version'],
    queryFn: fetchUpdateState,
    refetchInterval: 60_000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const deployedVersion = data?.deployedVersion ?? null;
  const requiredBackend = data?.requiredBackend ?? null;
  const backendBuild = data?.backendBuild ?? null;

  const newFrontend =
    RUNNING_VERSION !== 'dev' &&
    deployedVersion != null &&
    deployedVersion !== 'dev' &&
    deployedVersion !== RUNNING_VERSION;

  // Backend is ready when the deployed frontend declares no requirement (older
  // version.json) or the live backend build matches the required SHA. If /health is
  // unreachable while a requirement exists, treat as NOT ready (backend likely deploying).
  const backendReady = requiredBackend == null || backendBuild === requiredBackend;

  if (!newFrontend || !backendReady) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center justify-center gap-3 bg-brand-600 px-4 py-2 text-sm font-medium text-white">
      <span>A new version of Catalyst PSA is available.</span>
      <Button
        variant="secondary"
        className="h-7 px-3 py-0 text-xs"
        onClick={() => window.location.reload()}
      >
        Refresh to update
      </Button>
    </div>
  );
}
